import { QuoteCreationParams } from '@brenger/api-client';
import * as Sentry from '@sentry/react';
import { AnyAction, TaskAbortError } from '@reduxjs/toolkit';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { clarity } from '../../../configs/clarity';
import { r } from '../../../routes';
import { AppListenerApi, startAppListening } from '../../../state/middlewares/listener';
import { SessionStorageKeys, StopType } from '../../../types';
import * as appUtils from '../../../utils/basics';
import { dummyItemSet } from '../../../utils/global';
import { translate } from '../../../utils/localization';
import { indicativePriceClient, priceClient } from '../../../utils/request';
import {
  actions,
  getPriceCallRetryAttempts,
  getPriceRequestParams,
  getQuote,
  getStopDestinationDetails,
  getValidItemSets,
} from '../ducks';
import { GeneralFlowActionTypes } from '../typings';

const isRealQuote = (action: AnyAction): boolean =>
  action.type === GeneralFlowActionTypes.SET_QUOTE && !action.payload.removePrice;

const resetPriceUnlessBlocked = (api: AppListenerApi): void => {
  const q = getQuote(api.getState());
  // When the TR is blocked we don't show prices, but there can blocked reasons that we would like to keep around
  // So only reset when blocked is false:
  if (!q?.blocked?.result) api.dispatch(actions.resetPrice());
};

/**
 * Fetches the quote for the current flow input and stores it (same behavior as the former priceHandler saga).
 * A fetch superseded by a later GET_PRICE stops at its pending quote call, so an older quote never replaces
 * a newer one.
 */
export async function fetchPrice(api: AppListenerApi): Promise<void> {
  const priceParams = getPriceRequestParams(api.getState());
  let secondTryPriceParams: QuoteCreationParams | null = null;
  if (priceParams === null) {
    resetPriceUnlessBlocked(api);
    return;
  }
  try {
    api.dispatch(actions.setPriceLoading(true));
    api.dispatch(actions.hideNotification());

    let quote = await api.pause(priceClient.quotes.create(priceParams));

    /**
     * When a locality change was detected we fetch a new quote
     * Because of a distance change there is a chance that the TR became a flex dates
     * If so we like to reset the TR date choice and fetch a new Quote
     */
    // This is only present when locality has been changed (so user sees a modal)
    const localityChange = api.getState().generalTransport.localityChange;
    // if localitchange present and TR was not a flex dates before and now is a flex dates we fetch a new quote
    if (localityChange && localityChange.is_flex_dates_old === false && quote.flexible_dates?.result === true) {
      clarity.setTag('became_flex_dates', 'true');
      // Reset date choices
      api.dispatch(actions.resetChoice(['pickup_date', 'delivery_date']));
      secondTryPriceParams = getPriceRequestParams(api.getState());
      // the items or an address went away while the first quote was in flight
      if (secondTryPriceParams === null) {
        resetPriceUnlessBlocked(api);
        return;
      }
      const firstQuote = quote;
      // Fetch a new quote
      quote = await api.pause(priceClient.quotes.create(secondTryPriceParams));
      Sentry.addBreadcrumb({
        message: 'became_flex_dates_after_locality_change',
        data: { localityChange, firstQuote, secondQuote: quote },
      });
      // End becoming flex after locality change
    } else if (quote.flexible_dates?.result) {
      clarity.setTag('flex_dates', 'true');
      Sentry.addBreadcrumb({
        message: 'became_flex_dates',
        data: { localityChange, quote },
      });
    }

    // pricing may answer without a price (QuoteResponse.price is optional)
    if (typeof quote.price?.incl_vat?.amount === 'number') {
      clarity.setTag('price_api_response');
      api.dispatch(actions.setPriceLoading(false));
      // a price came through: earlier failures no longer count towards giving up
      if (getPriceCallRetryAttempts(api.getState()) > 0) api.dispatch(actions.resetRetryAttempts());
      api.dispatch(actions.setQuote(quote));
      // See useActiveDiscount hook for more details
      // This is mainly to keep the discount percentage in sync with the quote
      api.dispatch(actions.setDiscountPercentageBasisPoints(quote.discount?.percentage_basis_points ?? null));
    } else {
      appUtils.logError(`Quote without a price amount`);
      api.dispatch(actions.setPriceLoading(false));
    }
  } catch (e) {
    if (e instanceof TaskAbortError) return;
    appUtils.logError(e);
    // we handle retry process in axios error handling
  }
}

/**
 * Fetches an indicative quote (dummy item) once both addresses are known, or the real price when there are items
 * (same behavior as the former getQuoteOnDestinationChange saga).
 * The indicative quote is dropped when a newer destination change supersedes it or a real quote lands first.
 */
export async function fetchQuoteOnDestinationChange(api: AppListenerApi): Promise<void> {
  const itemSets = getValidItemSets(api.getState());
  if (itemSets.length && itemSets[0].items.length) {
    api.dispatch(actions.getPrice());
    return;
  }
  const pickup = getStopDestinationDetails(api.getState(), StopType.PICKUP);
  const delivery = getStopDestinationDetails(api.getState(), StopType.DELIVERY);

  if (!pickup?.latitude || !delivery?.latitude) {
    return;
  }

  const params: QuoteCreationParams = {
    source_flow: 'General',
    pickup: {
      address: {
        country_code: pickup.country_code,
        administrative_area: pickup.administrative_area,
        locality: pickup.locality,
        lat: pickup.latitude,
        lng: pickup.longitude,
      },
    },
    delivery: {
      address: {
        country_code: delivery.country_code,
        administrative_area: delivery.administrative_area,
        locality: delivery.locality,
        lat: delivery.latitude,
        lng: delivery.longitude,
      },
    },
    item_sets: [dummyItemSet],
  };
  try {
    const quote = await api.pause(
      Promise.race([indicativePriceClient.quotes.create(params), api.take(isRealQuote).then(() => null)])
    );
    if (quote) api.dispatch(actions.setQuote(quote, true));
  } catch (e) {
    if (e instanceof TaskAbortError) return;
    appUtils.logError(e);
  }
}

/**
 * Asks for a price on the flow steps that show one (same behavior as the former pathsThatShouldTriggerPriceCalls saga).
 */
export function pathsThatShouldTriggerPriceCalls(action: LocationChangeAction, api: AppListenerApi): void {
  const triggerRouterAction = action.payload.isFirstRendering || action.payload.action === 'PUSH';
  const triggerPaths = [
    r.generalFlow.items.index(),
    r.generalFlow.pickup.index(),
    r.generalFlow.help.index(),
    r.generalFlow.contact.index(),
    r.generalFlow.terms.index(),
    r.generalFlow.time.index(),
    r.generalFlow.time.delivery(),
  ].includes(action.payload.location.pathname);
  if (!triggerPaths || !triggerRouterAction) {
    return;
  }
  api.dispatch(actions.getPrice());
}

/**
 * Clears the flow session storage and the choices (same behavior as the former resetState saga).
 */
export function resetState(api: AppListenerApi): void {
  // When resetting we also clear the transaction event flag
  sessionStorage.removeItem(SessionStorageKeys.IS_TRANSACTION_EVENT_FIRED);
  // We also need to reset the landing page item executed, so we can anticipate again
  sessionStorage.removeItem(SessionStorageKeys.LANDING_PAGE_ITEM_ACTION);
  // Same for the product selection form
  sessionStorage.removeItem(SessionStorageKeys.PRODUCT_SELECTION_FORM);
  sessionStorage.removeItem(SessionStorageKeys.BRENGER_FORM);

  api.dispatch(actions.resetChoice('ALL'));
}

/**
 * Registers the GeneralFlow price, destination-quote and reset side effects
 * (same behavior as the former LOCATION_CHANGE, RESET_FLOW, PRICE_API_RETRY_ATTEMPT_INCREMENT, GET_PRICE and
 * SET_DESTINATION_DETAILS watchers of generalSaga).
 */
export const startPricingListeners = (): void => {
  startAppListening({
    type: LOCATION_CHANGE,
    effect: (action, api) => pathsThatShouldTriggerPriceCalls(action as LocationChangeAction, api),
  });

  startAppListening({
    type: GeneralFlowActionTypes.RESET_FLOW,
    effect: (_action, api) => resetState(api),
  });

  /**
   * Asks for the price again after attempts × 1s, or gives up after the fourth attempt with a Sentry report and an
   * error notification (same behavior as the former retryPriceHandler saga).
   */
  startAppListening({
    type: GeneralFlowActionTypes.PRICE_API_RETRY_ATTEMPT_INCREMENT,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      // check retry attempt < 3 or not show error
      const retryAttempts = getPriceCallRetryAttempts(api.getState());
      if (retryAttempts <= 3) {
        await api.delay(retryAttempts * 1000);
        api.dispatch(actions.getPrice());
      } else {
        // reported here: the failed call's catch only runs after the counter is reset below
        appUtils.logException(`Failed to get a price after 4 attempts`, {
          numberOfAttempts: retryAttempts,
          payload: getPriceRequestParams(api.getState()),
          rawItemFormValues: api.getState().generalTransport.product_selection,
        });
        clarity.setTag('price_api_failure');
        api.dispatch(actions.resetRetryAttempts());
        api.dispatch(actions.setPriceLoading(false));
        api.dispatch(
          actions.popNotification({
            type: 'error',
            message: translate('request_flow.price.failedAttempts'),
          })
        );
      }
    },
  });

  /**
   * Debounce (250ms) GET_PRICE; a later burst also drops a running fetch's quote.
   */
  startAppListening({
    type: GeneralFlowActionTypes.GET_PRICE,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      await api.delay(250);
      await fetchPrice(api);
    },
  });

  /* Debounce to prevent useless pricing calls */
  startAppListening({
    type: GeneralFlowActionTypes.SET_DESTINATION_DETAILS,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      await api.delay(100);
      await fetchQuoteOnDestinationChange(api);
    },
  });
};
